export const VAR_GROUPS: { label: string; vars: { token: string; text: string }[]; cls?: string }[] = [
  { label: 'Oslovení', vars: [
    { token: '[% shared.VOCATIVE %]', text: 'Vokativ' },
  ] },
  { label: 'Speciální', cls: 'special', vars: [{ token: '[% shared.UNSUBSCRIBE_URL %]', text: 'Odhlašovací odkaz' }] },
];

export const DEFAULT_HTML = `<p>Dobrý den, [% shared.VOCATIVE %],</p>
<p>…</p>
<p style="font-size:12px;color:#9ca3af">Pokud si nepřejete dostávat tyto e-maily, <a href="[% shared.UNSUBSCRIBE_URL %]">odhlaste se</a>.</p>`;

const SAMPLE: Record<string, string> = { VOCATIVE: 'Danieli', UNSUBSCRIBE_URL: '#' };

function renderConditions(s: string, vars: Record<string, string>): string {
  return s.replace(
    /\[%\s*IF\s+shared\.(\w+)\s*-?%\]([\s\S]*?)(?:\[%\s*ELSE\s*-?%\]([\s\S]*?))?\[%\s*END\s*-?%\]/gi,
    (_m, k: string, a: string, b?: string) => (vars[k.toUpperCase()] ? a : b ?? ''),
  );
}

export function sampleVars(s: string | null | undefined): string {
  return renderConditions(String(s ?? ''), SAMPLE)
    .replace(/\[%\s*shared\.([A-Z0-9_]+)\s*-?%\]/gi, (_m, k: string) => SAMPLE[k.toUpperCase()] ?? '')
    .replace(/href\s*=\s*(["'])\s*\[SIGNOUT\]\s*\1/gi, 'href="#"')
    .replace(/\[SIGNOUT\]/g, '#');
}

export function renderPreview(html: string): string {
  let out = renderConditions(html, SAMPLE).replace(/\[%\s*shared\.([A-Z0-9_]+)\s*-?%\]/gi, (_m, k: string) => SAMPLE[k.toUpperCase()] ?? '');
  out = out.replace(/href\s*=\s*(["'])\s*\[SIGNOUT\]\s*\1/gi, 'href="#"');
  out = out.replace(/\[SIGNOUT\]/g, '<a href="#">Odhlásit odběr</a>');
  return out;
}

export const SPAM_KEYWORDS = [
  'ZDARMA', 'BEZPLATNĚ', 'EXKLUZIVNÍ', 'POSLEDNÍ ŠANCE', 'OMEZENÁ NABÍDKA',
  'KLIKNI HNED', 'NAKUP TEĎ', 'VYHRAJTE', 'VÝHRA ZARUČENA', 'ZARUČENĚ',
  'FREE!!!', 'CLICK NOW', 'BUY NOW', 'LAST CHANCE', 'LIMITED OFFER',
  '100% GUARANTEE', 'WINNER', 'ACT NOW', '$$$', '!!!',
];

export interface SpamCat { name: string; details: string[]; score: number; level: 'ok' | 'warn' | 'bad' }

export function spamAudit(subject: string, html: string): { score: number; cats: SpamCat[]; verdict: string; level: 'excellent' | 'ok' | 'risk'; blocked: boolean } {
  const cats: SpamCat[] = [];
  let total = 0;
  const addCat = (name: string, rawScore: number, details: string[]) => {
    total += rawScore;
    const s = Math.round(rawScore * 10) / 10;
    cats.push({ name, score: s, details, level: s === 0 ? 'ok' : s >= 2 ? 'bad' : 'warn' });
  };

  const text = html.replace(/<style[\s\S]*?<\/style>/gi, '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ');
  const found: string[] = [];
  for (const kw of SPAM_KEYWORDS) {
    const re = new RegExp(kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    const matches = (text.match(re) || []).length + (subject.match(re) || []).length;
    if (matches > 0) found.push(`${kw} (${matches}×)`);
  }
  addCat('Spam keywords', Math.min(3, found.length * 0.5), found.length ? found : ['Žádná spam slova']);

  const subjDetails: string[] = [];
  let subjScore = 0;
  const subjLen = subject.length;
  subjDetails.push(`Délka ${subjLen} znaků`);
  if (subjLen < 20) { subjScore += 0.5; subjDetails.push('Krátký subject (< 20 zn.) - méně informativní'); }
  if (subjLen > 60) { subjScore += 0.5; subjDetails.push('Dlouhý subject (> 60 zn.) - bude oříznutý na mobilu'); }
  const upperLen = subject.replace(/[^A-ZÁ-Ž]/g, '').length;
  const lowerLen = subject.replace(/[^a-zá-ž]/g, '').length;
  if (upperLen > 5 && upperLen > lowerLen) { subjScore += 2; subjDetails.push('SUBJECT JE V CAPS LOCK - silný spam signál'); }
  const exclam = (subject.match(/!/g) || []).length;
  if (exclam >= 3) { subjScore += 1.5; subjDetails.push(`${exclam}× vykřičník v subjectu`); }
  if (subjLen >= 20 && subjLen <= 60 && upperLen <= 5 && exclam < 3) subjDetails.unshift('Subject vypadá OK');
  addCat('Předmět', subjScore, subjDetails);

  const htmlLen = html.length;
  const kb = Math.round((htmlLen / 1024) * 10) / 10;
  const weightDetails = [`Velikost ${kb} KB`];
  let wScore = 0;
  if (htmlLen < 5 * 1024) { wScore += 0.5; weightDetails.push('Pod 5 KB - možná příliš krátký mail'); }
  else if (htmlLen > 100 * 1024 && htmlLen <= 150 * 1024) { wScore += 1.5; weightDetails.push('Nad 100 KB - Gmail ořezává mail ("clipped")'); }
  else if (htmlLen > 150 * 1024) { wScore += 3; weightDetails.push('Nad 150 KB - Gmail/Outlook ořezává, deliverability silně klesá'); }
  else weightDetails.push('Velikost OK (5–100 KB)');
  addCat('HTML velikost', wScore, weightDetails);

  const imgCount = (html.match(/<img\b/gi) || []).length;
  const textLen = text.trim().length;
  const ratioDetails = [`${imgCount} obrázků · ${textLen} znaků textu`];
  let rScore = 0;
  if (imgCount > 0 && textLen < 100) { rScore += 3; ratioDetails.push('Pouze obrázky bez textu - silný spam signál'); }
  else if (imgCount > 5 && textLen < 500) { rScore += 1.5; ratioDetails.push('Hodně obrázků, málo textu'); }
  else if (imgCount > 10 && textLen < 1500) { rScore += 0.5; ratioDetails.push('Vysoký poměr obrázků'); }
  else ratioDetails.push('Poměr obrázek/text OK');
  addCat('Obrázky vs text', rScore, ratioDetails);

  const linkRe = /<a\s+[^>]*href\s*=\s*["']([^"']*)["'][^>]*>/gi;
  const links: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = linkRe.exec(html))) links.push(m[1]!);
  const shortener = ['bit.ly', 't.co', 'tinyurl.com', 'ow.ly', 'goo.gl', 'rebrand.ly'];
  const shortenerHits = links.filter((l) => shortener.some((s) => l.includes(s)));
  const domains = new Set<string>();
  links.forEach((l) => {
    try { domains.add(new URL(l.replace(/\[%[\s\S]*?%\]/g, 'https://x.example').replace('[SIGNOUT]', 'https://x.example')).hostname); } catch { return; }
  });
  const linkDetails = [`${links.length} odkazů · ${domains.size} domén`];
  let lScore = 0;
  if (links.length > 20) { lScore += 1; linkDetails.push('Více než 20 odkazů - spam filtr alert'); }
  else if (links.length > 15) { lScore += 0.5; linkDetails.push('Více odkazů (15+)'); }
  if (shortenerHits.length > 0) { lScore += shortenerHits.length; linkDetails.push(`${shortenerHits.length}× shortener (bit.ly apod.) - špatná deliverability`); }
  if (lScore === 0) linkDetails.push('Odkazy OK');
  addCat('Odkazy', lScore, linkDetails);

  const hasMarkedPreheader = /<span\s+data-preheader[^>]*>\s*\S[\s\S]{0,200}?<\/span>/i.test(html);
  const hasGenericPreheader = /<(?:span|div)[^>]*style\s*=\s*["'][^"']*display\s*:\s*none[^"']*["'][^>]*>[\s\S]{20,200}?<\/(?:span|div)>/i.test(html);
  const hasPreheader = hasMarkedPreheader || hasGenericPreheader;
  addCat('Preheader', hasPreheader ? 0 : 0.5, hasPreheader
    ? ['Skrytý preheader nalezen - bude vidět v inbox preview']
    : ['Chybí skrytý preheader - inbox preview vezme prvních ~80 znaků těla. Doporučujeme vyplnit preheader v kroku 1.']);

  const score = Math.min(10, Math.round(total * 10) / 10);
  const level: 'excellent' | 'ok' | 'risk' = score <= 3 ? 'excellent' : score <= 6 ? 'ok' : 'risk';
  const verdict = level === 'excellent' ? 'Výborné - pošli klidně' : level === 'ok' ? 'OK - zvažujeme opravu' : 'Vysoké riziko spam filtru';
  return { score, cats, verdict, level, blocked: score > 6 };
}

export interface ValidationResult { level: 'ok' | 'warn' | 'err'; messages: string[] }

export function validateHtml(html: string): ValidationResult {
  const msgs: string[] = [];
  let level: ValidationResult['level'] = 'ok';
  const bump = (l: ValidationResult['level']) => { level = level === 'err' || l === 'err' ? 'err' : 'warn'; };
  if (!html.trim()) return { level: 'err', messages: ['HTML obsah je prázdný.'] };
  const open = (html.match(/\[%/g) ?? []).length;
  const close = (html.match(/%\]/g) ?? []).length;
  if (open !== close) { msgs.push(`Nepárové [% %] značky (${open} otevíracích, ${close} zavíracích).`); level = 'err'; }
  if (!/\[SIGNOUT\]|\[%\s*shared\.UNSUBSCRIBE_URL\s*-?%\]/i.test(html)) { msgs.push('Chybí odhlašovací odkaz [% shared.UNSUBSCRIBE_URL %] (povinné, bez něj kampaň nelze odeslat).'); level = 'err'; }
  if (!/<a\s/i.test(html)) { msgs.push('HTML neobsahuje žádný odkaz <a>.'); bump('warn'); }
  if (!/\[%\s*shared\.VOCATIVE\s*-?%\]/i.test(html)) {
    msgs.push('Chybí oslovení [% shared.VOCATIVE %] - bez vokativu nelze kampaň odeslat.'); level = 'err';
  }
  const unknown = [...html.matchAll(/\[%\s*(?:IF\s+)?shared\.([A-Z0-9_]+)\s*-?%\]/gi)].map((x) => x[1]!.toUpperCase()).filter((k) => k !== 'VOCATIVE' && k !== 'UNSUBSCRIBE_URL');
  if (unknown.length) {
    msgs.push(`Nepodporovaná proměnná: ${[...new Set(unknown)].join(', ')} - k dispozici je jen VOCATIVE a UNSUBSCRIBE_URL.`);
    level = 'err';
  }
  if (level === 'ok') msgs.push('HTML je v pořádku - proměnné spárované, oslovení i odhlašovací odkaz přítomny.');
  return { level, messages: msgs };
}

const PREHEADER_RE = /<span data-preheader[^>]*>[\s\S]*?<\/span>\s*/i;

function preheaderSpan(text: string): string {
  const esc = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<span data-preheader style="display:none!important;visibility:hidden;opacity:0;color:transparent;height:0;width:0;overflow:hidden;mso-hide:all">${esc}</span>`;
}

export function composeBody(html: string, preheader: string): string {
  const body = html.replace(PREHEADER_RE, '');
  const ph = preheader.trim();
  if (!ph) return body;
  const span = preheaderSpan(ph);
  if (/<body[^>]*>/i.test(body)) return body.replace(/(<body[^>]*>)/i, `$1\n${span}`);
  return `${span}\n${body}`;
}

export function splitPreheader(htmlBody: string): { preheader: string; body: string } {
  const m = htmlBody.match(/<span data-preheader[^>]*>([\s\S]*?)<\/span>/i);
  if (!m) return { preheader: '', body: htmlBody };
  const txt = (m[1] ?? '').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
  return { preheader: txt, body: htmlBody.replace(PREHEADER_RE, '') };
}
